import React, { useState, useEffect, useRef } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Input,
  VStack,
  HStack,
  Text,
  Box,
  Kbd,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react'
import { 
  CheckCircleIcon, 
  ExternalLinkIcon, 
  RepeatIcon,
  StarIcon,
  DownloadIcon,
  ViewIcon,
  MoonIcon,
  SunIcon,
} from '@chakra-ui/icons'

interface Command {
  id: string
  name: string
  icon: any
  shortcut?: string
  action: () => void
}

interface CommandPaletteProps {
  isOpen: boolean
  onClose: () => void
  commands: Command[]
}

export default function CommandPalette({ isOpen, onClose, commands }: CommandPaletteProps) {
  const [search, setSearch] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  const bgColor = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const hoverBg = useColorModeValue('gray.100', 'gray.700')
  const selectedBg = useColorModeValue('blue.50', 'blue.900')
  const textColor = useColorModeValue('gray.800', 'gray.100')
  const mutedColor = useColorModeValue('gray.600', 'gray.400')

  const filteredCommands = commands.filter(cmd =>
    cmd.name.toLowerCase().includes(search.toLowerCase())
  )

  useEffect(() => {
    if (isOpen) {
      setSearch('')
      setSelectedIndex(0)
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [isOpen])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex((prev) => 
          prev < filteredCommands.length - 1 ? prev + 1 : prev
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex((prev) => prev > 0 ? prev - 1 : prev)
        break
      case 'Enter':
        e.preventDefault()
        if (filteredCommands[selectedIndex]) {
          filteredCommands[selectedIndex].action()
          onClose()
        }
        break
      case 'Escape':
        e.preventDefault()
        onClose()
        break
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent
        bg={bgColor}
        borderRadius="lg"
        overflow="hidden"
        mt="20vh"
        mx={4}
      >
        <ModalBody p={0}>
          <Box borderBottom="1px" borderColor={borderColor} p={3}>
            <Input
              ref={inputRef}
              placeholder="Type a command or search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              variant="unstyled"
              fontSize="lg"
              color={textColor}
              _placeholder={{ color: mutedColor }}
            />
          </Box>
          <VStack align="stretch" spacing={0} maxH="60vh" overflowY="auto" p={2}>
            {filteredCommands.length === 0 ? (
              <Text p={4} color={mutedColor} textAlign="center">
                No commands found
              </Text>
            ) : (
              filteredCommands.map((command, index) => (
                <HStack
                  key={command.id}
                  p={3}
                  spacing={3}
                  cursor="pointer"
                  borderRadius="md"
                  bg={index === selectedIndex ? selectedBg : 'transparent'}
                  _hover={{ bg: hoverBg }}
                  onClick={() => {
                    command.action()
                    onClose()
                  }}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <Icon as={command.icon} boxSize={5} color={mutedColor} />
                  <Text flex={1} fontWeight="medium" color={textColor}>
                    {command.name}
                  </Text>
                  {command.shortcut && (
                    <HStack spacing={1}>
                      {command.shortcut.split('+').map((key, i) => (
                        <Kbd key={i} fontSize="xs">
                          {key}
                        </Kbd>
                      ))}
                    </HStack>
                  )}
                </HStack>
              ))
            )}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
} 